// Wrappers over Web Speech API (TTS + STT).

let _voices = [];
let _voicesReady = false;

function _loadVoices() {
  if (!("speechSynthesis" in window)) return;
  _voices = window.speechSynthesis.getVoices() || [];
  _voicesReady = _voices.length > 0;
}
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  _loadVoices();
  window.speechSynthesis.onvoiceschanged = _loadVoices;
}

export function getVoices() {
  if (!_voicesReady) _loadVoices();
  return _voices;
}

// Voice quality filter: hide robotic Microsoft voices from the Profile dropdown.
// Users kept selecting them and complaining they sound too machine-like. The
// Google / Apple / Natural voices are consistently smoother.
export function isAcceptableVoice(v) {
  if (!v) return false;
  if (!/^en/i.test(v.lang)) return false;
  const name = (v.name || "").toLowerCase();
  // Block ONLY the legacy Microsoft SAPI voices (Zira/David/Mark/Hazel —
  // robotic). The newer "Microsoft X Online (Natural)" voices are Azure
  // Neural TTS and sound excellent. Edge users see ONLY Microsoft voices in
  // the engine list, so blocking all of them left Edge with nothing. The
  // Natural/Online ones are now allowed so Edge users get real quality.
  const legacyMicrosoft = /^microsoft\s+(david|mark|zira|hazel|hedda|irina|richard|sean|george|susan|catherine|james|linda)\b/;
  if (legacyMicrosoft.test(name)) return false;
  return true;
}

// Prefer Google US English, then any non-Microsoft English voice. Used as the
// implicit default when the user hasn't picked a voice in Profile.
export function pickDefaultVoice() {
  const voices = getVoices();
  const en = voices.filter(isAcceptableVoice);
  if (en.length === 0) return null;
  return (
    en.find((v) => /google us english/i.test(v.name)) ||
    en.find((v) => /google.*english/i.test(v.name)) ||
    en.find((v) => /samantha|karen|daniel|moira|aaron/i.test(v.name)) || // Apple natural voices
    // Edge / Windows: fall back to Microsoft Natural/Online voices. Aria is
    // the closest analogue to Google US English in warmth and clarity.
    en.find((v) => /microsoft\s+aria.*(natural|online)/i.test(v.name)) ||
    en.find((v) => /microsoft.*(natural|online)/i.test(v.name)) ||
    en.find((v) => /natural/i.test(v.name)) ||
    en.find((v) => /^en-us/i.test(v.lang)) ||
    en[0]
  );
}

export async function waitForVoices(timeoutMs = 1500) {
  if (_voicesReady) return _voices;
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      _loadVoices();
      if (_voicesReady || Date.now() - start > timeoutMs) return resolve(_voices);
      setTimeout(tick, 100);
    };
    tick();
  });
}

// Split text into TTS-safe chunks at sentence/clause boundaries.
// Why so short? Both Chrome (desktop) and the Android WebView's speech engine
// silently kill utterances longer than ~12-15 seconds of audio. Even worse,
// some Android builds clip utterances over ~150 chars regardless of duration.
// Keeping each chunk under 160 chars on mobile (≈ 7-9 seconds of speech) is
// the only thing that reliably reads long passages end-to-end. We pick the
// limit based on user agent — desktop tolerates 220, mobile gets 160.
function _ttsMaxLen() {
  if (typeof navigator === "undefined") return 200;
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
  return isMobile ? 160 : 220;
}
function splitForTTS(text, maxLen) {
  if (!text) return [];
  const limit = maxLen || _ttsMaxLen();
  if (text.length <= limit) return [text];
  // Split on sentence boundaries (keep the terminator attached)
  const sentences = text.match(/[^.!?]+[.!?]+\s*|[^.!?]+$/g) || [text];
  const out = [];
  let buf = "";
  for (let s of sentences) {
    // If a single sentence is still too long, split it on commas / semicolons / " and "
    if (s.length > limit) {
      const parts = s.split(/(?<=[,;])\s+|\s+(?:and|but|or|which|that)\s+/i);
      for (const p of parts) {
        if ((buf + " " + p).length > limit && buf) { out.push(buf.trim()); buf = p; }
        else buf = buf ? `${buf} ${p}` : p;
      }
    } else if ((buf + s).length > limit && buf) {
      out.push(buf.trim()); buf = s;
    } else {
      buf += s;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

// Chrome bug workaround: speechSynthesis pauses internally after ~15s of speaking.
// Poking pause/resume every 10 seconds keeps the engine alive through long chunks.
// https://bugs.chromium.org/p/chromium/issues/detail?id=679437
let _keepAliveTimer = null;
function _startKeepAlive() {
  _stopKeepAlive();
  _keepAliveTimer = setInterval(() => {
    try {
      const s = window.speechSynthesis;
      if (!s) return _stopKeepAlive();
      if (s.speaking) { s.pause(); s.resume(); }
      else _stopKeepAlive();
    } catch { _stopKeepAlive(); }
  }, 10000);
}
function _stopKeepAlive() {
  if (_keepAliveTimer) { clearInterval(_keepAliveTimer); _keepAliveTimer = null; }
}

// --- Cancellation epoch ---
// cancelSpeech() bumps _epoch, which invalidates any pending setTimeout work
// scheduled inside an earlier speak() call. This is critical: the browser's
// speechSynthesis queue persists across React unmounts / route changes / even
// after logout. Without epoch guarding, a delayed primer or queueReal fallback
// can re-populate the queue AFTER cancelSpeech ran, causing a stuck read-loop
// the user can only escape by leaving the site. Do NOT remove this guard.
let _epoch = 0;
const _pendingTimers = new Set();
function _schedule(fn, ms) {
  const scheduledEpoch = _epoch;
  const id = setTimeout(() => {
    _pendingTimers.delete(id);
    if (scheduledEpoch !== _epoch) return; // superseded by a cancelSpeech() call
    try { fn(); } catch { /* non-fatal */ }
  }, ms);
  _pendingTimers.add(id);
  return id;
}
function _currentEpoch() { return _epoch; }

export function speak(text, { rate = 1, pitch = 1, onend, onstart, voiceURI } = {}) {
  if (!("speechSynthesis" in window)) return null;
  const synth = window.speechSynthesis;
  const wasSpeaking = synth.speaking || synth.pending;
  synth.cancel();
  _stopKeepAlive();

  const cleaned = cleanForTTS(text);
  const chunks = splitForTTS(cleaned);
  if (chunks.length === 0) {
    if (onend) setTimeout(onend, 10);
    return null;
  }

  // Snapshot the current epoch. If cancelSpeech() runs before our async work
  // completes, this value will no longer match _currentEpoch() and every
  // downstream callback becomes a no-op. Prevents the "ghost reader" bug.
  const myEpoch = _currentEpoch();
  const alive = () => myEpoch === _currentEpoch();

  let voice = null;
  if (voiceURI) {
    const found = getVoices().find((x) => x.voiceURI === voiceURI);
    // Respect the user's explicit pick unless it's a blocked (Microsoft) voice —
    // in which case fall back to the preferred default instead of reading robotic.
    voice = (found && isAcceptableVoice(found)) ? found : pickDefaultVoice();
  } else {
    voice = pickDefaultVoice();
  }

  // Chrome's speechSynthesis clips the first 200-900ms after cancel(). Diagnosis
  // from the field: volume=0 utterances get short-circuited by the engine (no
  // real audio processing = no warm-up). The only reliable fix is a REAL word
  // at very-low-but-non-zero volume, gated on onend so we KNOW the engine is
  // fully initialized before the real audio begins.
  const utterances = chunks.map((chunk, i) => {
    const u = new SpeechSynthesisUtterance(chunk);
    u.rate = rate;
    u.pitch = pitch;
    if (voice) u.voice = voice;
    if (i === 0 && onstart) u.onstart = () => { if (alive()) onstart(); };
    if (i === chunks.length - 1) {
      u.onend = () => { _stopKeepAlive(); if (alive() && onend) onend(); };
      u.onerror = () => { _stopKeepAlive(); if (alive() && onend) onend(); };
    } else {
      u.onerror = () => { /* let the next chunk keep going */ };
    }
    return u;
  });

  // Primer: "ok" at 1% volume, fast. At 1% volume on any normal listening
  // setup (phone speaker, laptop, headphones at regular levels) this is
  // effectively inaudible — but the engine genuinely processes the audio,
  // fully warming the pipeline so the first real word arrives intact.
  const primer = new SpeechSynthesisUtterance("ok");
  primer.volume = 0.01;
  primer.rate = 1.5;
  primer.pitch = pitch;
  if (voice) primer.voice = voice;

  // Queue real utterances ONLY after primer.onend fires — zero race conditions.
  let queued = false;
  const queueReal = () => {
    if (queued) return;
    queued = true;
    if (!alive()) return; // cancelled while primer was warming up
    try {
      _startKeepAlive();
      for (const u of utterances) synth.speak(u);
    } catch { /* non-fatal */ }
  };
  primer.onend = queueReal;
  primer.onerror = queueReal;

  const delay = wasSpeaking ? 320 : 100;
  _schedule(() => {
    try { synth.speak(primer); } catch { /* non-fatal */ }
    // Safety net: if primer.onend never fires, queue real audio after 600ms
    _schedule(queueReal, 600);
  }, delay);
  return utterances[0];
}

export function cancelSpeech() {
  // Bump epoch FIRST so any in-flight setTimeout callbacks become no-ops
  // the instant they fire. Without this, orphan timers re-populate the
  // speechSynthesis queue after cancel() and the user hears a stuck read-loop
  // that survives React unmount / logout / route-change.
  _epoch++;
  _pendingTimers.forEach((id) => clearTimeout(id));
  _pendingTimers.clear();
  _stopKeepAlive();
  if ("speechSynthesis" in window) {
    try { window.speechSynthesis.cancel(); } catch { /* non-fatal */ }
  }
}

// Expose for top-level safety nets (AuthContext logout, pagehide, route change).
// Kept as a no-arg function so callers don't accidentally pass stale state.
if (typeof window !== "undefined") {
  // Hard-cancel TTS if the tab is hidden/closed. Browser would normally keep
  // the queue alive when the tab goes into bfcache.
  window.addEventListener("pagehide", () => { try { cancelSpeech(); } catch {} });
  window.addEventListener("beforeunload", () => { try { cancelSpeech(); } catch {} });
}

// Preprocess text so TTS doesn't awkwardly read "Q:" or "A:" as letters.
export function cleanForTTS(text = "") {
  if (!text) return "";
  return String(text)
    // Strip Markdown formatting that screen readers / TTS would otherwise
    // pronounce as literal punctuation ("asterisk asterisk bold asterisk
    // asterisk"). Order matters: do bold/italic before backticks because
    // ``code`` runs would steal the asterisks otherwise.
    .replace(/```[\s\S]*?```/g, " ")        // fenced code blocks → drop
    .replace(/`([^`]+)`/g, "$1")             // inline `code` → keep contents
    .replace(/\*\*([^*]+)\*\*/g, "$1")       // **bold**
    .replace(/__([^_]+)__/g, "$1")           // __bold__
    .replace(/\*([^*\n]+)\*/g, "$1")          // *italic*
    .replace(/(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])/g, "$1") // _italic_
    .replace(/~~([^~]+)~~/g, "$1")           // ~~strikethrough~~
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // ![alt](url) → alt only
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")  // [text](url) → text only
    .replace(/^#{1,6}\s+/gm, "")              // # headings → text
    .replace(/^>\s+/gm, "")                   // > blockquote prefix
    .replace(/^[-*+]\s+/gm, "")               // bullet markers
    // "Q14:" / "Q.1)" / "Question 3:" → drop
    .replace(/\b(?:Q(?:uestion)?)\s*\d*\s*[:.\-)]\s*/gi, "")
    // "A:" / "Ans:" / "A)" → turn into a breath pause so the letter isn't spoken
    .replace(/\b(?:A(?:nswer|ns)?)\s*[:.\-)]\s+/gi, ". ")
    // bullet prefixes "a)" "b)" etc at line start
    .replace(/(^|\n)\s*[a-eA-E]\s*[\)\.]\s*/g, "$1")
    // Spell out years (1000–2099) so TTS says "seventeen eighty-one", not
    // "one thousand seven hundred eighty-one". Block only when the number is
    // clearly a decimal (1.5) or part of a larger number (1,781 / 12345) —
    // sentence-ending periods or trailing commas are fine.
    .replace(/(?<![\d.,])(1\d{3}|20\d{2})(?![.,]?\d)/g, (m) => _yearToWords(parseInt(m, 10)))
    // collapse runs of whitespace
    .replace(/\s{2,}/g, " ")
    .trim();
}

// --- Year → words helpers (used by cleanForTTS) ---
const _ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const _TEENS = ["ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const _TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

function _twoDigitWords(n) {
  if (n < 10) return _ONES[n];
  if (n < 20) return _TEENS[n - 10];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? _TENS[t] : `${_TENS[t]}-${_ONES[o]}`;
}

function _yearToWords(n) {
  // 2000–2009: "two thousand", "two thousand five"
  if (n >= 2000 && n <= 2009) {
    return n === 2000 ? "two thousand" : `two thousand ${_ONES[n - 2000]}`;
  }
  // Everything else 1000–1999 and 2010–2099: read as two pairs
  const hi = Math.floor(n / 100);
  const lo = n % 100;
  const hiW = _twoDigitWords(hi);
  if (lo === 0) return `${hiW} hundred`; // "1800" → "eighteen hundred"
  if (lo < 10) return `${hiW} oh ${_ONES[lo]}`; // "1904" → "nineteen oh four"
  return `${hiW} ${_twoDigitWords(lo)}`; // "1781" → "seventeen eighty-one"
}

export function createRecognizer({ onResult, onEnd, onError, continuous = false } = {}) {
  const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Rec) return null;
  const rec = new Rec();
  rec.lang = "en-US";
  rec.interimResults = true;
  rec.continuous = continuous;
  rec.maxAlternatives = 1;
  rec.onresult = (e) => {
    let finalText = "", interimText = "";
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) finalText += t + " ";
      else interimText += t + " ";
    }
    onResult?.({ final: finalText.trim(), interim: interimText.trim() });
  };
  rec.onend = () => onEnd?.();
  rec.onerror = (e) => onError?.(e);
  return rec;
}

// Local word-overlap similarity (0..1) — used in "exact" mode and as a fallback.
// Handles "/" "|" "," separated alternates: "Temporary/Volatile" is treated as
// two valid answers — the best match wins.
export function similarity(a = "", b = "") {
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  const splitAlternates = (s) => String(s).split(/[/|,]|(?:\s+or\s+)/i).map((x) => x.trim()).filter(Boolean);

  const targets = splitAlternates(b);
  const spokens = splitAlternates(a);
  if (targets.length === 0 || spokens.length === 0) return 0;

  let best = 0;
  for (const t of targets) {
    const B = norm(t);
    if (B.length === 0) continue;
    const setB = new Set(B);
    for (const s of spokens) {
      const A = norm(s);
      if (A.length === 0) continue;
      let matched = 0;
      A.forEach((w) => { if (setB.has(w)) matched++; });
      const p = matched / A.length;
      const r = matched / B.length;
      const f1 = p + r === 0 ? 0 : (2 * p * r) / (p + r);
      // Bonus: single-word target and user says that word → full credit
      const contains = B.length === 1 && A.includes(B[0]) ? 1 : 0;
      const score = Math.max(f1, contains);
      if (score > best) best = score;
    }
  }
  return best;
}

// Detect voice commands in a transcript. Returns one of: "next", "prev", "repeat", "pause", "stop", "hint", null.
export function detectVoiceCommand(text = "") {
  const t = text.toLowerCase().trim();
  if (!t) return null;
  const words = t.split(/\s+/);
  // Allow a short phrase (<=6 words) or match when the transcript STARTS with a command verb.
  const head = words.slice(0, 3).join(" ");
  const tail = words.slice(-3).join(" ");
  const scan = (pattern) => pattern.test(t) && (words.length <= 6 || pattern.test(head) || pattern.test(tail));
  if (scan(/\b(?:hint|help\s*(?:me)?|clue|give\s+(?:me\s+)?a\s+hint)\b/)) return "hint";
  if (scan(/\b(?:prev(?:ious)?|go\s+back|back(?:\s+(?:up|one))?|last\s+one)\b/)) return "prev";
  if (scan(/\b(?:next|skip(?:\s+this(?:\s+one)?)?|move\s+on|go\s+(?:to\s+)?next|continue(?:\s+please)?)\b/)) return "next";
  if (scan(/\b(?:repeat(?:\s+that)?|again|replay|say\s+again|one\s+more\s+time)\b/)) return "repeat";
  if (scan(/\b(?:pause|hold\s+on|wait(?:\s+a\s+(?:sec|moment))?)\b/)) return "pause";
  if (scan(/\b(?:stop|exit|quit|end\s+session)\b/)) return "stop";
  return null;
}

// --- Audio cues for pass/fail / listening states ---
let _audioCtx = null;
function _ctx() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return null; }
  }
  if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
  return _audioCtx;
}

function _beep(freqs = [600], durationMs = 160, gain = 0.08) {
  const ctx = _ctx();
  if (!ctx) return;
  // User-configurable volume multiplier (0..1.5) stored in localStorage
  let mult = 1;
  try {
    const v = parseFloat(localStorage.getItem("echo_cue_volume"));
    if (!isNaN(v)) mult = Math.max(0, Math.min(1.5, v));
  } catch {}
  const effectiveGain = gain * mult;
  if (effectiveGain <= 0) return;
  const now = ctx.currentTime;
  freqs.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = f;
    osc.connect(g);
    g.connect(ctx.destination);
    const start = now + i * (durationMs / 1000);
    const stop = start + durationMs / 1000;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(effectiveGain, start + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, stop);
    osc.start(start);
    osc.stop(stop + 0.01);
  });
}

export const cues = {
  pass: () => _beep([660, 990], 160, 0.12),       // brighter ascending chime
  fail: () => _beep([320, 260], 200, 0.10),       // softer descending buzz
  // Clear, louder two-tone "your turn" cue — like a walkie-talkie over
  listenStart: () => _beep([660, 990], 180, 0.18),
  listenEnd: () => _beep([520], 90, 0.06),        // short
};
